import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import { ErrorModal, getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { Button, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { upsertCobble } from "../repositories";

const moment = getMomentTz();

type Props = {
	action: "Delete" | "Undelete" | "";
	onHide: () => void;
};

export const ConfirmActionModal = ({ action, onHide }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [operatorId, setOperatorId] = useState<number | null>(null);
	const [comment, setComment] = useState("");

	const { shiftId: shiftIdParam } = useParams();
	const [params] = useSearchParams();
	const includeCorrections = params.get("includeCorrections") === "true";
	const cobbleId = params.get("cobbleId");

	const queryClient = useQueryClient();
	const upsertcobble = useMutation(upsertCobble, {
		onSuccess: async () => {
			setNotificationMessage("The cobble was corrected succesfully");
			await queryClient.invalidateQueries([
				"cobbles",
				Number(shiftIdParam),
				includeCorrections,
			]);
			setOperatorId(null);
			setComment("");
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {},
	});

	return (
		<>
			<MesfModal
				title={`CONFIRM ${action.toUpperCase()}`}
				open={action !== ""}
				id="confirm-modal"
				handleClose={() => onHide()}
			>
				<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={1}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<OperatorSelector
								label="Operator"
								value={operatorId}
								onChange={(id: number) => setOperatorId(id)}
							/>
						</Grid2>
						{action === "Delete" && (
							<Grid2 size={{ md: 12, xs: 12 }}>
								<TextField
									label="Comment"
									value={comment}
									onChange={(e) => setComment(e.target.value)}
									multiline
									minRows={2}
									maxRows={4}
									fullWidth
									size="small"
									inputProps={{ maxLength: 200 }}
									helperText={`${comment.length}/200`}
								/>
							</Grid2>
						)}
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide()}
								disabled={isSubmitLoading}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								disabled={isSubmitLoading || !operatorId}
								onClick={() => {
									upsertcobble.mutate({
										cobbleId: Number(cobbleId),
										operatorId: operatorId!,
										isDelete: action === "Delete",
										comment: comment || null,
									});
								}}
							>
								Confirm
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
